<script>
    import { findContacts } from '../../api/contacts';
    import SingleSelector from "../SingleSelector.vue";
    import SearchInput from "../SearchInput.vue";
    import ContactsMapAzure from "./map/ContactsMapAzure.vue";
    import bus from "../../utils/eventBus";
    import { debounce as _debounce } from 'lodash';
    import { toRaw } from 'vue';

    export default {
        components: {
            "single-selector": SingleSelector,
            "search-input": SearchInput,
            "contacts-map": ContactsMapAzure
        },
        props: {
            pageLang: { type: String, default: '' },
            continents: { type: Array, default: () => [] },
            countries: { type: Array, default: () => [] },
            defaultCountryValue: { type: Number, default: null },
            defaultCompanyTypeValue: { type: Number, default: null },
            defaultContinentValue: { type: Number, default: null },
            defaultLocationTypeValue: { type: Number, default: null },
            defaultOfferingValue: { type: Number, default: null },
            defaultIndustryValue: { type: Number, default: null },
            hideAllFilters: { type: Boolean, default: false },
            defaultListView: { type: Boolean, default: false },
            hideMap: { type: Boolean, default: false },
            singleContact: { type: Object, default: null },
            selectedCountries: { type: Array, default: () => [] }
        },
        data: function () {
            return {
                bus: bus,
                isInitializing: true,
                selectedContinent: null,
                selectedCountry: null,
                countriesByContinent: [],
                preselectedCountries: this.selectedCountries,
                selectedState: null,
                filteredCountries: this.countries,
                contacts: [],
                industries: [],
                offerings: [],
                locationTypes: [],
                companyTypes: [],
                preselectedCountryValue: this.defaultCountryValue,
                selectedOffice: null,
                selectedOfficeIndex: null,
                selectedIndustry: null,
                selectedOffering: null,
                selectedLocationType: null,
                selectedCompanyType: null,
                isFacilityFilterOn: false,
                filteredOffices: [],
                filteredOfficesByType: [],
                isLoading: false,
                // datalayer list of categories
                searchFiltersCategories: [
                    "continent",
                    "country",
                    "industry",
                    "offering",
                    "location type",
                    "company type",
                ],
                navigationCardsHash: null,
                searchQuery: '',
                searchMinLength: 3,
                autocompleteVisible: false,
                showMap: !this.defaultListView && !this.hideMap,
                showMoreFilters: false,
                listViewDefaultLength: 5,
                showAllOffices: false,
                listViewHasSelectedOffice: false,
                showMapContactDetail: true,
                isMobile: window.innerWidth < 722
            };
        },
        created() {
            window.addEventListener('click', (e) => {
                this.bus.$emit('hideOtherDropdowns', e.target);
            });

            window.addEventListener('navigation-cards-hash-change', (e) => {
                if (this.companyTypes && this.companyTypes.length > 0) {
                    this.findInCompanyTypes(e.detail);
                }
                else {
                    this.navigationCardsHash = e.detail;
                }
                this.scrollToPosition('#contactCountrySelection');
            }, false);

            if (!this.singleContact) {
                const query = this.currentQuery || {};
                // Store initial URL parameters before any processing
                const urlCountry = query.country ? parseInt(query.country) : null;
                const urlOffice = query.office ? parseInt(query.office) : null;
                const urlCompany = query.company ? parseInt(query.company) : null;
                const urlFacility = query.facility ? parseInt(query.facility) : null;
                if (urlCountry) {
                    this.getPreselectedCountry(urlCountry);
                }
                else {
                    this.getPreselectedCountry(this.preselectedCountryValue);
                }

                if (urlCompany !== null) {
                    this.selectCompanyType({ value: urlCompany })
                }
                else if (urlFacility) {
                    this.isFacilityFilterOn = true;
                }

                if (this.defaultContinentValue !== null && this.defaultContinentValue !== -1 && this.defaultCountryValue == null) {
                    this.handleContinentChange({ value: this.defaultContinentValue });
                }
                else {
                    this.loadDefaultMarkers();
                }
            }
            else {
                this.handleDefaultSingleContact();
            }
        },
        mounted() {
            if (location.hash !== '') {
                if (location.hash === '#0') {
                    this.dispatchHashChangeEvent(0);
                }
                else if (location.hash === '#1') {
                    this.dispatchHashChangeEvent(1);
                }
                else {
                    this.scrollToPosition(location.hash);
                }
            }

            if (this.defaultCompanyTypeValue !== null && this.defaultCompanyTypeValue !== -1 && !this.$route.query.company) {
                this.selectCompanyType({ value: this.defaultCompanyTypeValue });
            }
            if (this.defaultLocationTypeValue !== null && this.defaultLocationTypeValue !== -1) {
                this.selectLocationType({ value: this.defaultLocationTypeValue });
            }
            if (this.defaultOfferingValue !== null && this.defaultOfferingValue !== -1) {
                this.selectOffering({ value: this.defaultOfferingValue });
            }
            if (this.defaultIndustryValue !== null && this.defaultIndustryValue !== -1) {
                this.selectIndustry({ value: this.defaultIndustryValue });
            }

            if (!this.hideAllFilters) {
                this.bus.$on('dropdownChange', (val, id) => {
                    this.closeOfficeDetail();
                    if (id === 'continents') {
                        this.handleContinentChange(val);
                    }
                    else if (id === 'countries') {
                        this.handleCountryChange(val);
                    }
                    else if (id === 'states') {
                        this.selectState(val);
                    }
                    else if (id === 'industries') {
                        this.selectIndustry(val);
                    }
                    else if (id === 'offerings') {
                        this.selectOffering(val);
                    }
                    else if (id === 'locationTypes') {
                        this.selectLocationType(val);
                    }
                    else if (id === 'companyTypes') {
                        this.selectCompanyType(val);
                    }
                });
            }

            window.addEventListener('resize', _debounce(() => {
                this.isMobile = window.innerWidth < 722;
                initAccordions();
            }, 500));
        },
        computed: {
            showClearAll: function () {
                return this.selectedContinent || this.selectedCountry || this.selectedState || (this.selectedCompanyType && this.selectedCompanyType.value !== -1) || (this.selectedIndustry && this.selectedIndustry.value !== -1) || (this.selectedOffering && this.selectedOffering.value !== -1) || (this.selectedLocationType && this.selectedLocationType.value !== -1) || (this.searchQuery && this.searchQuery.length >= this.searchMinLength);
            },
            showNoResults: function () {
                return this.filteredOffices.length == 0 && !this.isLoading;
            },
            showStatesDropdown: function () {
                return this.selectedCountry && Array.isArray(this.selectedCountry.states) && this.selectedCountry.states.length > 1;
            },
            showContactInfo: function () {
                return this.selectedOffice && (this.selectedOffice.phone || this.selectedOffice.email || this.selectedOffice.webAddress || this.selectedOffice.address)
            },
            showListView: function () {
                return !this.showMap && this.filteredOffices.length > 0;
            },
            showLegend: function () {
                return this.selectedCompanyType === null && !this.isFacilityFilterOn;
            },
            filteredOfficesList: function () {
                return this.showAllOffices ? this.filteredOffices : this.filteredOffices.slice(0, this.listViewDefaultLength);
            },
            showShowMoreBtn: function () {
                return this.showListView && !this.listViewHasSelectedOffice && !this.showAllOffices && (this.filteredOffices && this.filteredOffices.length > this.listViewDefaultLength)
            },
            hasOfferings: function () {
                return this.selectedOffice && ((this.selectedOffice.offeringNames && this.selectedOffice.offeringNames.length > 0))
            },
            hasMultipleServices: function () {
                return this.selectedOffice && ((this.selectedOffice.additionalCountries && this.selectedOffice.additionalCountries.length > 0))
            },
            hasDescription: function () {
                return this.selectedOffice && this.selectedOffice.description && !this.stringIsNullOrWhitespace(this.selectedOffice.description)
            },
            hasSalesContact: function () {
                return this.selectedOffice && this.selectedOffice.salesContactDetails
            },
            currentRoute() {
                return (this.$router && this.$router.currentRoute) || {};
            },
            currentQuery() {
                if (this.currentRoute.query) {
                    return this.currentRoute.query;
                }
                const params = new URLSearchParams(window.location.search);
                const q = {};
                params.forEach((v, k) => { q[k] = v; });
                return q;
            }
        },
        updated() {
            if (!this.singleContact && !this.isInitializing) {
                this.$router.push({
                    path: window.location.pathname,
                    query: {
                        country: this.selectedCountry ? this.selectedCountry.value : undefined,
                        office: this.selectedOffice ? this.selectedOffice.id : undefined,
                        company: this.selectedCompanyType && this.selectedCompanyType.value !== -1 ? this.selectedCompanyType.value : undefined,
                        facility: this.isFacilityFilterOn ? 1 : undefined
                    }
                }).catch(err => { });
            }

            if (!this.hideAllFilters) {
                // updating list of categories for datalayer
                if (this.showStatesDropdown && this.searchFiltersCategories.indexOf('state') === -1) {
                    this.searchFiltersCategories.push('state');
                }
                else if (!this.showStatesDropdown && this.searchFiltersCategories.indexOf('state') !== -1) {
                    this.searchFiltersCategories = this.searchFiltersCategories.filter(function (item) {
                        return item !== 'state';
                    })
                }
            }
        },
        watch: {
            selectedCountry: function () {
                this.searchOffices({
                    rootIds: this.countriesByContinent.length > 0 ? this.countriesByContinent.toString() : this.getSelectedCountries().toString(),
                    language: this.pageLang,
                    q: this.searchQuery
                });
                this.countriesByContinent = [];
            },
            selectedOffice: function (newVal) {
                if (newVal) {
                    this.selectedOfficeIndex = this.filteredOffices.indexOf(newVal) + 1;
                    this.$nextTick(() => {
                        this.dataLayerOfficeDetailDisplayed('search_result_view');
                    });
                }
                else {
                    this.selectedOfficeIndex = null;
                }
                this.$nextTick(() => {
                    initAccordions();
                });
            },
            filteredOffices: function () {
                this.listViewHasSelectedOffice = false;
                this.$nextTick(() => {
                    if (this.autocompleteVisible) {
                        this.dataLayerSearchImpressionsShown();
                    }
                });
                this.filteredOfficesByType = [];
                if (!this.selectedCompanyType && !this.isFacilityFilterOn && this.companyTypes && this.companyTypes.length) {
                    this.companyTypes.forEach(type => {
                        if (type.value !== -1) {
                            this.filteredOfficesByType.push({
                                'name': type.name,
                                'items': this.filteredOffices.filter(item => item.companyType === type.value)
                            });
                        }
                    });
                }
            },
            showMap: function () {
                this.$nextTick(() => {
                    this.dataLayerViewSwitch();
                    initAccordions();
                });
            }
        },
        methods: {
            handleContinentChange: function (continent) {
                this.selectedContinent = continent;
                this.selectedState = null;
                this.countriesByContinent = [];
                this.filteredCountries = [];
                for (let i = 0; i < this.countries.length; i++) {
                    if (this.countries[i].continent === continent.id) {
                        this.filteredCountries.push(this.countries[i]);
                        this.countriesByContinent.push(this.countries[i].value);
                    }
                }
                // trigger search only when there is no country selected, otherwise it will be triggered on watch method for selectedCountry
                if (this.selectedCountry === null) {
                    this.searchOffices({
                        rootIds: this.countriesByContinent.toString(),
                        language: this.pageLang,
                        q: this.searchQuery
                    });
                    this.countriesByContinent = [];
                }
                this.selectedCountry = null;
            },
            handleCountryChange: function (country) {
                this.selectedState = null;

                let selected = country;
                if (country && typeof country === 'object') {
                    const key = (country.value !== undefined && country.value !== null) ? country.value : ((country.id !== undefined && country.id !== null) ? country.id : null);
                    if (key !== null) {
                        const match = this.countries.find(c => c.value === key || c.id === key);
                        if (match) selected = match;
                    }
                }

                this.selectedCountry = selected;

                if (this.selectedCountry) {
                    const continentKey = this.selectedCountry.continent;
                    const contMatch = this.continents.find(cont => cont.value === continentKey || cont.id === continentKey);
                    if (contMatch) this.selectedContinent = contMatch;
                }

                this.$nextTick(() => {
                    this.dataLayerOfficesFiltered();
                })
            },
            onSearchInput(val) {
                this.searchQuery = val;
                if (this.searchQuery && this.searchQuery.length >= this.searchMinLength || this.searchQuery === '') {
                    this.autocompleteVisible = true;
                    this.showAllOffices = false;
                    this.searchOffices({
                        rootIds: this.getSelectedCountries().toString(),
                        language: this.pageLang,
                        q: this.searchQuery
                    });
                    this.$nextTick(() => {
                        this.dataLayerSearchQueryChange();
                    })
                }
            },
            loadDefaultMarkers() {
                this.searchOffices({
                    rootIds: this.getSelectedCountries().toString(),
                    language: this.pageLang,
                    q: ''
                });
            },
            searchOffices(params) {

                this.isLoading = true;
                findContacts(params)
                    .then(response => {
                        this.isLoading = false;
                        this.contacts = response.contacts;
                        this.industries = response.industries;
                        this.offerings = response.offerings;
                        this.locationTypes = response.locationTypes;
                        this.companyTypes = response.companyTypes;
                        this.filterOffices();
                        this.$nextTick(() => {
                            if (this.navigationCardsHash !== null) {
                                if (this.companyTypes && this.companyTypes.length > 0) {
                                    this.findInCompanyTypes(this.navigationCardsHash);
                                }
                                this.navigationCardsHash = null;
                            }
                            initAccordions();
                        });

                        if (this.$route.query.office) {
                            this.setSelectedOffice(parseInt(this.$route.query.office));
                        }

                        // Mark initialization as complete after data is loaded
                        this.$nextTick(() => {
                            this.isInitializing = false;
                        });
                    })
                    .catch(() => {
                        this.isLoading = false;
                        // Mark initialization as complete even on error
                        this.$nextTick(() => {
                            this.isInitializing = false;
                        });
                    })
            },
            handleSelectOfficeClick(id) {

                if (!this.showMap) {
                    this.showMap = true;
                    this.mapPointSelection(id);

                    this.$nextTick(() => {
                        var $elm = $(this.$refs.contactsMap.$el);
                        if ($elm.length) {
                            $('html, body').animate({
                                scrollTop: ($elm.offset().top - 180)
                            }, 1000);
                        }
                    });
                }

                this.$nextTick(() => {
                    this.dataLayerContactInteraction('directions');
                    this.dataLayerOfficeDetailDisplayed('search_result_click');
                });

            },
            handleCompanyTypeClick(companyType) {
                this.clearSelectedOffice();
                this.isFacilityFilterOn = false;
                if (this.selectedCompanyType && companyType.value === this.selectedCompanyType.value) {
                    this.selectCompanyType(null)
                }
                else {
                    this.selectCompanyType(companyType)
                }
            },
            handleFacilityFilterClick() {
                this.clearSelectedOffice();

                this.isFacilityFilterOn = !this.isFacilityFilterOn;
                this.selectedCompanyType = null;
                this.filterOffices();
            },
            selectOffice(contact) {
                this.selectedOffice = contact;
            },
            selectState: function (state) {
                this.selectedState = state;
                this.clearSelectedOffice();
                this.filterOffices();
            },
            selectIndustry(industry) {
                this.selectedIndustry = industry;
                this.filterOffices();
            },
            selectOffering(offering) {
                this.selectedOffering = offering;
                this.filterOffices();
            },
            selectLocationType(location) {
                this.selectedLocationType = location;
                this.filterOffices();
            },
            selectCompanyType(company) {
                this.selectedCompanyType = company;
                this.filterOffices();
            },
            findInCompanyTypes(id) {
                var match = null;
                this.companyTypes.forEach((item) => {
                    if (item.value === id) {
                        match = item;
                    }
                });
                if (match !== null) {
                    this.selectCompanyType(match);
                }
                else {
                    this.clearAll();
                }
            },
            filterOffices() {
                this.filteredOffices = [];
                const contacts = toRaw(this.contacts) || this.contacts;
                const getNumericFromSelection = (sel, canonicalList) => {
                    if (!sel && sel !== 0) return null;
                    const normalized = normalizeDropdown(sel, canonicalList);
                    if (!normalized) return null;
                    const val = normalized.value ?? normalized.id;
                    return (val !== undefined && val !== null) ? Number(val) : null;
                };

                const selIndustry = getNumericFromSelection(this.selectedIndustry, this.industries);
                const selOffering = getNumericFromSelection(this.selectedOffering, this.offerings);
                const selLocationType = getNumericFromSelection(this.selectedLocationType, this.locationTypes);
                const selCompanyType = getNumericFromSelection(this.selectedCompanyType, this.companyTypes);
                const selState = toRaw(this.selectedState);
                (contacts || []).forEach(contact => {
                    const rawContact = toRaw(contact) || contact;
                    const industries = Array.isArray(rawContact.industries) ? toRaw(rawContact.industries) : (rawContact.industries ? toRaw(rawContact.industries) : []);
                    const offerings = Array.isArray(rawContact.offerings) ? toRaw(rawContact.offerings) : (rawContact.offerings ? toRaw(rawContact.offerings) : []);
                    const locationTypes = Array.isArray(rawContact.locationTypes) ? toRaw(rawContact.locationTypes) : (rawContact.locationTypes ? toRaw(rawContact.locationTypes) : []);

                    const industryMatch = (!selIndustry || selIndustry === -1) || (industries && industries.includes(Number(selIndustry)));
                    const offeringMatch = (!selOffering || selOffering === -1) || (offerings && offerings.includes(Number(selOffering)));
                    const locationMatch = (!selLocationType || selLocationType === -1) || (locationTypes && locationTypes.includes(Number(selLocationType)));
                    const companyMatch = ((!selCompanyType && selCompanyType !== 0) || selCompanyType === -1) || (rawContact.companyType === Number(selCompanyType));
                    const stateMatch = (!selState) || (rawContact.state === selState.name);
                    const facilityMatch = (!this.isFacilityFilterOn) || rawContact.isMetsoFacility;

                    if (industryMatch && offeringMatch && locationMatch && companyMatch && stateMatch && facilityMatch) {
                        this.filteredOffices.push(rawContact);
                    }
                });
                this.$nextTick(() => {
                    this.dataLayerOfficesFiltered();
                })
            },
            handleClearAllClick() {
                this.clearAll();
                this.$nextTick(() => {
                    this.dataLayerOfficesFiltered();
                })
            },
            clearAll() {
                this.selectedCountry = null;
                this.selectedContinent = null;
                this.selectedState = null;
                this.selectedIndustry = null;
                this.selectedOffering = null;
                this.selectedLocationType = null;
                this.selectedCompanyType = null;
                this.filteredCountries = this.countries;
                this.searchQuery = '';
                this.bus.$emit('clearAll');
                this.clearSelectedOffice();
                this.searchOffices({
                    rootIds: this.getSelectedCountries().toString(),
                    language: this.pageLang,
                    q: ''
                });
            },
            closeOfficeDetail() {
                this.showMapContactDetail = false;
                this.clearSelectedOffice();
            },
            clearSelectedOffice() {
                this.selectedOffice = null;
            },
            setSelectedOffice(id) {
                for (let contact of this.contacts) {
                    if (contact.id === id) {
                        this.selectOffice(contact);
                        this.showMapContactDetail = true;
                        break;
                    }
                };
            },
            getSelectedOfficeName(contact) {
                if (contact) {
                    if (contact.city) {
                        return contact.name + ', ' + contact.city
                    }
                    else {
                        return contact.name;
                    }
                }
            },
            getPreselectedCountry(val) {
                this.countries.forEach(country => {
                    if (country.value === val) {
                        this.selectedCountry = country;
                        if (!this.preselectedCountryValue) {
                            this.preselectedCountryValue = val;
                        }
                        this.continents.forEach(continent => {
                            if (continent.value === country.continent) {
                                this.selectedContinent = continent;
                            }
                        });
                        this.filteredCountries = [];
                        for (let i = 0; i < this.countries.length; i++) {
                            if (this.countries[i].continent === this.selectedContinent.value) {
                                this.filteredCountries.push(this.countries[i]);
                            }
                        }
                    }
                });
            },
            getCorrectOfficeWebsite(url) {
                if (url) {
                    if (url.startsWith('https://')) {
                        return url;
                    }
                    else if (url.startsWith('http://')) {
                        return url.replace('http', 'https')
                    }
                    else {
                        return 'https://' + url;
                    }
                }
            },
            getMapLink(office) {
                if (office && office.googleMapsLink) {
                    return office.googleMapsLink;
                }
                else if (office && office.address) {
                    return '//www.google.com/maps/place/' + office.city + ' ' + office.address.split(' ').join('+');
                }
            },
            renderMapLink: function (office) {
                if (window.innerWidth >= 722) {
                    return this.getMapLink(office);
                }
                else {
                    return null;
                }
            },
            handleGetDirectionsLinkClick: function (office) {
                window.open(this.getMapLink(office), '_blank');
                this.$nextTick(() => {
                    this.dataLayerContactInteraction('directions');
                })
            },
            dataLayerContactInteraction(type) {
                if (dataLayer) {
                    dataLayer.push(
                        {
                            "event": "contact_interaction",
                            "interaction_type": type
                        }
                    );
                }
            },
            dataLayerOfficeDetailDisplayed(eventType) {
                if (dataLayer && this.selectedOffice) {
                    var searchCardInfo = {
                        "search_card_position": this.selectedOfficeIndex,
                        "search_card_target_url": window.location.href,
                        "search_card_heading": this.selectedOffice.name
                    };

                    if (dataLayer.find(({ event }) => event === eventType)) {
                        dataLayer.find(function (obj) {
                            if (obj.event === eventType) {
                                if (eventType === "search_result_view") {
                                    obj.search_impressions.push(searchCardInfo);
                                }
                                else if (eventType === "search_result_click") {
                                    obj.search_clicked_card.push(searchCardInfo);
                                }
                            }
                        })
                    }
                    else {
                        var selectedFilters = {
                            "country": this.selectedCountry,
                            "industry": this.selectedIndustry,
                            "offering": this.selectedOffering,
                            "location type": this.selectedLocationType,
                            "company type": this.selectedCompanyType
                        };
                        if (this.showStatesDropdown) {
                            selectedFilters.state = this.selectedState;
                        }
                        var data = {
                            "event": eventType,
                            "search_page_url": window.location.href,
                            "search_type": "main contact search",
                            "search_filters_visible": true,
                            "search_filter_categories": this.searchFiltersCategories,
                            "search_filters": selectedFilters
                        };
                        if (eventType === "search_result_view") {
                            data.search_impressions = [searchCardInfo];
                            data.visible_offices = this.filteredOffices;
                        }
                        else if (eventType === "search_result_click") {
                            data.search_clicked_card = [searchCardInfo];
                        }
                        dataLayer.push(data);
                    }
                }
            },
            dataLayerOfficesFiltered() {
                var selectedFilters = {
                    "country": this.selectedCountry,
                    "industry": this.selectedIndustry,
                    "offering": this.selectedOffering,
                    "location type": this.selectedLocationType,
                    "company type": this.selectedCompanyType
                };
                if (this.showStatesDropdown) {
                    selectedFilters.state = this.selectedState;
                }
                if (dataLayer) {
                    dataLayer.push({
                        "event": "search_results",
                        "search_page_url": window.location.href,
                        "search_type": "main contact search",
                        "search_filters_visible": true,
                        "search_filter_categories": this.searchFiltersCategories,
                        "search_filters": selectedFilters,
                    });
                }
            },
            dataLayerViewSwitch() {
                if (dataLayer) {
                    dataLayer.push(
                        {
                            "event": "contact_view_switch",
                            "view_type": this.showMap ? "map" : "list",
                            "search_page_url": window.location.href,
                        }
                    );
                }
            },
            dataLayerAllFiltersSwitch() {
                if (dataLayer) {
                    dataLayer.push(
                        {
                            "event": "contact_filters_switch",
                            "show_more_filters": this.showMoreFilters,
                            "search_page_url": window.location.href,
                        }
                    );
                }
            },
            dataLayerSearchQueryChange() {
                if (dataLayer) {
                    dataLayer.push(
                        {
                            "event": "contact_search_query",
                            "search_query": this.searchQuery,
                            "search_page_url": window.location.href,
                        }
                    );
                }
            },
            dataLayerSearchImpressionsShown() {
                if (dataLayer) {
                    dataLayer.push(
                        {
                            "event": "contact_search_impressions",
                            "search_impressions": this.filteredOffices,
                            "search_page_url": window.location.href,
                        }
                    );
                }
            },
            dataLayerSearchImpressionClick() {
                if (dataLayer) {
                    dataLayer.push(
                        {
                            "event": "contact_search_impression_click",
                            "search_impression": this.selectedOffice,
                            "search_page_url": window.location.href,
                        }
                    );
                }
            },
            scrollToPosition(id) {
                setTimeout(function () {
                    if (id.indexOf('#contact-sales-') != -1) {
                        var industryId = id.slice(-1);
                        window.addEventListener('DOMContentLoaded', function () {
                            $('[data-industry-id=' + industryId + ']').click();
                        });
                    }
                    else {
                        var $elm = $(id);
                        if ($elm.length) {
                            $('html, body').animate({
                                scrollTop: ($elm.offset().top - 2 * 69)
                            }, 1000);
                        }
                    }

                }, 500);
            },
            dispatchHashChangeEvent(id) {
                const hashevent = new CustomEvent('navigation-cards-hash-change', { detail: parseInt(id) });
                window.dispatchEvent(hashevent);
            },
            setMapView(show) {
                this.mapView = show;
            },
            handleAutocompleteSelection(item) {
                this.autocompleteVisible = false;
                this.showAllOffices = true;
                this.selectOffice(item);
                if (!this.showMap) {
                    this.listViewHasSelectedOffice = true;
                }
                this.$nextTick(() => {
                    this.dataLayerSearchImpressionClick();
                });
            },
            mapPointSelection(contact) {
                this.setSelectedOffice(contact);
            },
            handleShowMoreClick() {
                this.showMoreFilters = !this.showMoreFilters;
                this.$nextTick(() => {
                    this.dataLayerAllFiltersSwitch();
                });
            },
            handleShowAllClick: function () {
                this.showAllOffices = true;
                this.$nextTick(() => {
                    initAccordions();
                });
            },
            handleMapDetailOfferingsClick: function () {
                this.$nextTick(() => {
                    this.dataLayerContactInteraction('offerings');
                });
            },
            handleMapDetailMultipleServicesClick: function () {
                this.$nextTick(() => {
                    this.dataLayerContactInteraction('multiple-services');
                });
            },
            handleMapDetailDescriptionClick: function () {
                this.$nextTick(() => {
                    this.dataLayerContactInteraction('description');
                });
            },
            handleMapDetailSalesContactClick: function () {
                this.$nextTick(() => {
                    this.dataLayerContactInteraction('sales-contact');
                });
            },
            handleMapDetailContactClick: function () {
                this.$nextTick(() => {
                    this.dataLayerContactInteraction('contact');
                });
            },
            handleDefaultSingleContact() {
                this.contacts.push(this.singleContact);
                this.filteredOffices = this.contacts;
                this.selectOffice(this.singleContact);
                this.$nextTick(() => {
                    this.isInitializing = false;
                });
            },
            stringIsNullOrWhitespace(html) {
                let tmp = document.createElement("DIV");
                tmp.innerHTML = html;
                let result = tmp.textContent || tmp.innerText || "";
                return result === '' || result === ' ';
            },
            getSelectedCountries() {
                return this.selectedCountry ? [this.selectedCountry.value] : (this.preselectedCountries ? this.preselectedCountries : []);
            }
        }
    };
</script>